import { Node, mergeAttributes } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { EditorState } from '@tiptap/pm/state';

/**
 * 마크다운 테이블 Extension
 * - GFM 테이블 스펙만 지원 (셀 병합, 리사이즈 불가)
 * - 헤더 행 필수, 정렬만 지원
 */

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        markdownTable: {
            insertMarkdownTable: (options: { rows: number; cols: number }) => ReturnType;
            addMarkdownTableRow: () => ReturnType;
            deleteMarkdownTableRow: () => ReturnType;
            addMarkdownTableColumn: () => ReturnType;
            deleteMarkdownTableColumn: () => ReturnType;
            deleteMarkdownTable: () => ReturnType;
        };
    }
}

// 헬퍼: 현재 선택 위치에서 테이블 정보 찾기
function findTable(state: EditorState): { tablePos: number; tableNode: ProseMirrorNode } | null {
    const { selection } = state;
    let result: { tablePos: number; tableNode: ProseMirrorNode } | null = null;

    state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
        if (node.type.name === 'markdownTable') {
            result = { tablePos: pos, tableNode: node };
            return false;
        }
    });

    return result;
}

// 헬퍼: 현재 선택 위치에서 행 정보 찾기
function findRow(state: EditorState): { rowPos: number; rowNode: ProseMirrorNode; tableNode: ProseMirrorNode } | null {
    const { selection } = state;
    let tableNode: ProseMirrorNode | null = null;
    let rowPos: number | null = null;
    let rowNode: ProseMirrorNode | null = null;

    state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
        if (node.type.name === 'markdownTable') {
            tableNode = node;
        }
        if (node.type.name === 'markdownTableRow') {
            rowPos = pos;
            rowNode = node;
            return false;
        }
    });

    if (rowPos !== null && rowNode && tableNode) {
        return { rowPos, rowNode, tableNode };
    }
    return null;
}

// 헬퍼: 셀 생성
function createCell(state: EditorState, isHeader: boolean, text: string = ' ') {
    return state.schema.nodes.markdownTableCell.create({ isHeader }, state.schema.nodes.paragraph.create({}, state.schema.text(text)));
}

// 헬퍼: 테이블 안에 있는지 확인
function isInsideTable(state: EditorState): boolean {
    const { $from } = state.selection;
    for (let d = $from.depth; d > 0; d--) {
        if ($from.node(d).type.name === 'markdownTable') {
            return true;
        }
    }
    return false;
}

export const MarkdownTable = Node.create({
    name: 'markdownTable',
    group: 'block',
    content: 'markdownTableRow+',

    parseHTML() {
        return [{ tag: 'table' }];
    },

    addOptions() {
        return {
            HTMLAttributes: {
                // 이전 기본 스타일을 여기에 둘 수 있습니다.
                style: 'width: 100%; border-collapse: collapse; margin: 1rem 0;',
            },
        };
    },

    renderHTML({ HTMLAttributes }) {
        return ['table', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), ['tbody', 0]];
    },

    addCommands() {
        return {
            insertMarkdownTable:
                ({ rows, cols }) =>
                ({ commands, state }) => {
                    if (isInsideTable(state)) return false;

                    const createCellContent = (isHeader: boolean) => ({
                        type: 'markdownTableCell',
                        attrs: { isHeader },
                        content: [{ type: 'paragraph', content: [{ type: 'text', text: isHeader ? '헤더' : ' ' }] }],
                    });

                    const headerRow = {
                        type: 'markdownTableRow',
                        content: Array(cols)
                            .fill(null)
                            .map(() => createCellContent(true)),
                    };

                    const bodyRows = Array(rows - 1)
                        .fill(null)
                        .map(() => ({
                            type: 'markdownTableRow',
                            content: Array(cols)
                                .fill(null)
                                .map(() => createCellContent(false)),
                        }));

                    return commands.insertContent({
                        type: 'markdownTable',
                        content: [headerRow, ...bodyRows],
                    });
                },

            deleteMarkdownTable:
                () =>
                ({ state, dispatch }) => {
                    const table = findTable(state);
                    if (!table || !dispatch) return false;

                    dispatch(state.tr.delete(table.tablePos, table.tablePos + table.tableNode.nodeSize));
                    return true;
                },

            addMarkdownTableRow:
                () =>
                ({ state, dispatch }) => {
                    const table = findTable(state);
                    if (!table || !dispatch) return false;

                    const colCount = table.tableNode.firstChild?.childCount || 0;
                    if (colCount === 0) return false;

                    const newRow = state.schema.nodes.markdownTableRow.create(
                        {},
                        Array(colCount)
                            .fill(null)
                            .map(() => createCell(state, false)),
                    );

                    const insertPos = table.tablePos + table.tableNode.nodeSize - 1;
                    dispatch(state.tr.insert(insertPos, newRow));
                    return true;
                },

            deleteMarkdownTableRow:
                () =>
                ({ state, dispatch }) => {
                    const row = findRow(state);
                    if (!row || !dispatch) return false;

                    // 헤더 행 삭제 불가, 최소 2행 유지
                    if (row.tableNode.childCount <= 1) return false;
                    if (row.rowNode === row.tableNode.firstChild) return false;

                    dispatch(state.tr.delete(row.rowPos, row.rowPos + row.rowNode.nodeSize));
                    return true;
                },

            addMarkdownTableColumn:
                () =>
                ({ state, dispatch }) => {
                    const table = findTable(state);
                    if (!table || !dispatch) return false;

                    let tr = state.tr;
                    let offset = 0;

                    table.tableNode.forEach((row, rowOffset, rowIndex) => {
                        const newCell = createCell(state, rowIndex === 0, rowIndex === 0 ? '헤더' : ' ');
                        const rowEndPos = table.tablePos + 1 + rowOffset + row.nodeSize - 1;
                        tr = tr.insert(rowEndPos + offset, newCell);
                        offset += newCell.nodeSize;
                    });

                    dispatch(tr);
                    return true;
                },

            deleteMarkdownTableColumn:
                () =>
                ({ state, dispatch }) => {
                    const table = findTable(state);
                    if (!table || !dispatch) return false;

                    // 현재 셀 인덱스 찾기
                    let cellIndex = -1;
                    state.doc.nodesBetween(state.selection.from, state.selection.to, (node, _pos, parent, index) => {
                        if (node.type.name === 'markdownTableCell' && parent?.type.name === 'markdownTableRow') {
                            cellIndex = index;
                        }
                    });

                    if (cellIndex < 0) return false;

                    // 최소 1열 유지
                    const firstRow = table.tableNode.firstChild;
                    if (firstRow && firstRow.childCount <= 1) return false;

                    // 삭제할 셀 위치 수집
                    const deletions: { from: number; to: number }[] = [];
                    table.tableNode.forEach((row, rowOffset) => {
                        let cellOffset = 0;
                        row.forEach((cell, _offset, index) => {
                            if (index === cellIndex) {
                                const cellStart = table.tablePos + 1 + rowOffset + 1 + cellOffset;
                                deletions.push({ from: cellStart, to: cellStart + cell.nodeSize });
                            }
                            cellOffset += cell.nodeSize;
                        });
                    });

                    // 뒤에서부터 삭제
                    let tr = state.tr;
                    deletions.reverse().forEach(({ from, to }) => {
                        tr = tr.delete(from, to);
                    });

                    dispatch(tr);
                    return true;
                },
        };
    },

    addStorage() {
        return {
            markdown: {
                serialize(state: any, node: any) {
                    const rows: string[] = [];
                    node.forEach((row: any, _: any, index: number) => {
                        const cells: string[] = [];
                        row.forEach((cell: any) => {
                            cells.push(cell.textContent || ' ');
                        });
                        rows.push(`| ${cells.join(' | ')} |`);

                        // 첫 번째 행(헤더) 뒤에 구분선 추가
                        if (index === 0) {
                            const separators = cells.map(() => '---');
                            rows.push(`| ${separators.join(' | ')} |`);
                        }
                    });

                    state.write(rows.join('\n'));
                    state.closeBlock(node);
                },
            },
        };
    },
});

export const MarkdownTableRow = Node.create({
    name: 'markdownTableRow',
    content: 'markdownTableCell+',
    tableRole: 'row',

    parseHTML() {
        return [{ tag: 'tr' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['tr', mergeAttributes(HTMLAttributes), 0];
    },
});

export const MarkdownTableCell = Node.create({
    name: 'markdownTableCell',
    content: 'block+',
    tableRole: 'cell',

    addAttributes() {
        return {
            isHeader: { default: false },
            align: { default: 'left' },
        };
    },

    parseHTML() {
        return [
            { tag: 'th', getAttrs: () => ({ isHeader: true }) },
            { tag: 'td', getAttrs: () => ({ isHeader: false }) },
        ];
    },

    renderHTML({ node, HTMLAttributes }) {
        const { isHeader, align } = node.attrs;
        const tag = isHeader ? 'th' : 'td';
        const style = `text-align: ${align}; border: 1px solid #ddd; padding: 0.5rem;${isHeader ? ' background-color: #f5f5f5; font-weight: bold;' : ''}`;

        return [tag, mergeAttributes({ style }, HTMLAttributes), 0];
    },
});
